/**
 * The shell both unauthenticated screens sit in: brand and heading on the
 * left above whatever form the page supplies, product illustration on the
 * right.
 *
 * WHY THIS EXISTS. Login had been reskinned and reset-password had not, so
 * the two screens shared a URL prefix and nothing else -- login had the
 * logo, a dark gradient panel and a candidate-list illustration, while
 * reset-password had no logo, a light panel, a cartoon illustration from the
 * previous brand, and a different split (50/50 against login's 45/55). A
 * person following "Forgot password?" left one product and arrived at
 * another.
 *
 * WHY A COMPONENT RATHER THAN A SHARED STYLESHEET. The right-hand panel is
 * ~50 lines of markup for an illustration with no content of its own.
 * Sharing only the CSS would have meant copying that markup into the second
 * page, which is the duplication that let the two drift in the first place.
 *
 * WHAT IT DOES NOT DO. It does not own the form. antd needs the submit
 * button inside its own Form element, so each page keeps its own form and
 * takes `submitBtn` and `link` from this component's stylesheet -- see the
 * note there.
 */
import { memo, ReactNode } from 'react';

import styles from './index.module.css';

export interface Props {
  /** The page's heading, e.g. "Sign in". */
  title: string;
  /** Explanatory copy under the heading. Tightens the heading's margin. */
  subtitle?: ReactNode;
  /** The page's form. */
  children: ReactNode;
  /** A trailing link under the form, centred. */
  footer?: ReactNode;
}

/* The illustration's five colours are deliberately not tokenised, and the
   reason is worth keeping with them: this is a fake candidate list with score
   badges banded by value, and the design system has no score-band palette --
   so every available token would either flatten three distinct bands into one
   colour or assert a meaning the system does not define. The Candidate
   Scorecard tier defines score bands against real data; these should be
   revisited from that palette then, rather than guessed at now. */
const SAMPLE_ROWS = [
  { avatar: undefined, lines: ['80%', '55%'], score: 93, badge: '#1a6b3c' },
  { avatar: '#6a7fb0', lines: ['70%', '45%'], score: 90, badge: '#2563eb' },
  { avatar: '#4a6090', lines: ['65%', '50%'], score: 74, badge: '#e07b00' },
];

const AuthShell = ({ title, subtitle, children, footer }: Props) => (
  <div className={styles.wrapper}>
    <div className={styles.left}>
      <div className={styles.leftInner}>
        <div className={styles.logo}>
          <div className={styles.logoMark}>A</div>
          <span className={styles.logoText}>AVRIO AI</span>
        </div>
        <h1
          className={
            subtitle
              ? `${styles.title} ${styles.titleWithSubtitle}`
              : styles.title
          }
        >
          {title}
        </h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        {children}
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>

    <div className={styles.right}>
      <div className={styles.rightInner}>
        <p className={styles.tagline}>
          Job posting to candidate
          <br />
          interviews in minutes
        </p>
        <p className={styles.taglineSub}>with Avrio Intelligence</p>
        <div className={styles.card}>
          <div className={styles.cardHeader}>TOP CANDIDATES · PHARMACIST</div>
          {SAMPLE_ROWS.map((row) => (
            <div className={styles.candidateRow} key={row.score}>
              <div
                className={styles.avatar}
                style={row.avatar ? { background: row.avatar } : undefined}
              />
              <div className={styles.candidateLines}>
                {row.lines.map((width) => (
                  <div className={styles.line} style={{ width }} key={width} />
                ))}
              </div>
              <div
                className={styles.scoreBadge}
                style={{ background: row.badge }}
              >
                {row.score}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default memo(AuthShell);
